import { api } from './api';

export type MeetingParticipant = {
  user_id: number;
  role: string | null;
  approved: boolean;
  response?: string | null;
  full_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
};

export type MeetingEvent = {
  id: number;
  title: string;
  description?: string | null;
  event_type?: string | null;
  start_time: string; // ISO
  end_time: string; // ISO
  created_by_id?: number | null;
  chat_group_id?: number | null;
  participants: MeetingParticipant[];
};

export async function fetchMeeting(eventId: number): Promise<MeetingEvent> {
  const resp = await api.get(`/api/meetings/${eventId}`);
  return resp.data as MeetingEvent;
}

export async function ensureMeetingChat(eventId: number): Promise<{ conversation_id: string; chat_group_id: number }> {
  const resp = await api.post(`/api/meetings/${eventId}/chat`, {});
  return resp.data as { conversation_id: string; chat_group_id: number };
}

export type MyChildOption = { id: number; full_name: string; classroom_name?: string | null };

export async function fetchMyChildrenForMeeting(eventId: number): Promise<MyChildOption[]> {
  const resp = await api.get(`/api/meetings/${eventId}/my-children`);
  return Array.isArray(resp.data) ? (resp.data as MyChildOption[]) : [];
}

export async function addChildToMeeting(eventId: number, childId: number): Promise<{ added: 0 | 1 }> {
  const resp = await api.post(`/api/meetings/${eventId}/add-child`, { child_id: childId });
  return resp.data as { added: 0 | 1 };
}

export type MeetingPollOption = { id: number; text: string; votes: number };
export type MeetingPoll = {
  id: number;
  event_id: number;
  question: string;
  options: MeetingPollOption[];
  is_active: boolean;
  total_votes: number;
  my_vote_option_id?: number | null;
};

export async function fetchActiveMeetingPoll(eventId: number): Promise<MeetingPoll | null> {
  const resp = await api.get(`/api/meetings/${eventId}/poll`);
  return (resp.data ?? null) as MeetingPoll | null;
}

export async function createMeetingPoll(eventId: number, payload: { question: string; options: string[] }): Promise<MeetingPoll> {
  const resp = await api.post(`/api/meetings/${eventId}/poll`, payload);
  return resp.data as MeetingPoll;
}

export async function voteMeetingPoll(eventId: number, pollId: number, optionId: number): Promise<MeetingPoll> {
  const resp = await api.post(`/api/meetings/${eventId}/poll/${pollId}/vote`, { option_id: optionId });
  return resp.data as MeetingPoll;
}

export async function closeMeetingPoll(eventId: number, pollId: number): Promise<void> {
  await api.post(`/api/meetings/${eventId}/poll/${pollId}/close`, {});
}

export type MeetingFileItem = {
  id: number;
  name: string;
  type: string;
  size_bytes: number;
  size_label: string;
  uploaded_by?: string | null;
  uploaded_at: string;
  url: string;
};

export async function fetchMeetingFiles(eventId: number): Promise<MeetingFileItem[]> {
  const resp = await api.get(`/api/meetings/${eventId}/files`);
  return Array.isArray(resp.data) ? (resp.data as MeetingFileItem[]) : [];
}

export async function uploadMeetingFile(eventId: number, file: File): Promise<MeetingFileItem> {
  const form = new FormData();
  form.append('file', file);
  const resp = await api.post(`/api/meetings/${eventId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data as MeetingFileItem;
}
